import type { TextInputSuffix } from '@ksyos/design-system';
import type { Dispatch, SetStateAction } from 'react';
import {
    ActionBar,
    Box,
    Button,
    ButtonRow,
    Checkbox,
    DropdownFilter,
    Heading,
    Link,
    Loader,
    Pagination,
    Row,
    Table,
    TableBody,
    TableCell,
    TableCellSelectRow,
    TableContainer,
    TableHead,
    TableHeader,
    TableHeaderSelectAll,
    TableRow,
    Text,
    TextInput,
} from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useMemo, useState } from 'react';
import type { IDeviceType } from '@common/interfaces/deviceType';
import type { IDeviceFilters } from '@common/validation/device';
import type { IOrganization } from '@frontend/components/interfaces/organization';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { BadgeWithLabel } from '@frontend/components/elements/BadgeWithLabel';
import { Figure } from '@frontend/components/elements/figure/Figure';
import { fetchJson } from '@frontend/store/fetch';
import { hasOneAuthorization } from '@frontend/utils';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import type { EpisodeCategory } from '../../../../common/enums';
import { ManagerAuthorization } from '../../../../common/enums';
import { DeviceStatus } from '../../../../common/enums/deviceStatus';
import { StyledBox } from '../../../components/elements/styledBox';
import { getSelectedLanguage } from '../../../translate';
import { TextWithMatchingTermInBold } from '../../elements/common';
import { NullableFormattedDate } from '../../elements/NullableFormattedDate';

interface IDeviceTypeWithServices extends IDeviceType {
    services?: {
        episodeCategoryName: EpisodeCategory;
        id: string;
    }[];
}

export interface IDevice {
    deviceStatus: {
        status: DeviceStatus;
    };
    deviceStatusOrganizations: IDeviceStatusOrganization[];
    deviceType: IDeviceTypeWithServices;
    externalId: string;
    id: string;
}

export interface IDeviceStatusOrganization {
    id: string;
    issueDate: Date | string;
    organization: IOrganization | null;
}

interface DevicesListProps {
    setShouldBeReloaded: Dispatch<SetStateAction<boolean>>;
    shouldBeReloaded: boolean;
}

const emptyDeviceFilters: IDeviceFilters = {};

export function DevicesList(props: Readonly<DevicesListProps>): JSX.Element {
    const { shouldBeReloaded, setShouldBeReloaded } = props;
    const navigate = useNavigate();
    const [items, setItems] = useState<IDevice[]>([]);
    const [deviceTypeList, setDeviceTypeList] = useState<IDeviceTypeWithServices[]>([]);
    const [deviceFilters, setDeviceFilters] = useState<IDeviceFilters>(emptyDeviceFilters);
    const [episodeCategoryFilterValues, setEpisodeCategoryFilterValues] = useState<EpisodeCategory[]>([]);
    const [page, setPage] = useState<number>(1);
    const [total, setTotal] = useState<number>(0);
    const [itemsPerPage, setItemsPerPage] = useState<number>(15);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [areAllChecked, setAreAllChecked] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const abortableFetch = useAbortableFetch();
    const translate = getSelectedLanguage();

    const isAddEditAllowed = useMemo(() => hasOneAuthorization(ManagerAuthorization.devicesAddEdit), []);

    useEffect(() => {
        loadDeviceTypes();
        loadEpisodeCategoryFilterValues();
    }, []);

    useEffect(() => {
        loadDevices();
    }, [page, itemsPerPage]);

    useEffect(() => {
        if (areAllChecked) {
            setSelectedItems([]);
        }
    }, [areAllChecked]);

    useEffect(() => {
        if (selectedItems.length === total && total > 0) {
            setAreAllChecked(true);
        }
    }, [selectedItems]);

    useEffect(() => {
        if (shouldBeReloaded) {
            loadDevices();
        }
    }, [shouldBeReloaded]);

    const suffixProp: TextInputSuffix = {
        icon: 'search',
        labelText: 'aria labelled text',
        onSuffixClick: () => {
            applySearchFilter();
        },
    };

    return (
        <>
            <StyledBox>
                <TextInput
                    onChange={(e) => setDeviceFilters({ ...deviceFilters, searchText: e.target.value })}
                    onEnterKeyPress={applySearchFilter}
                    onReset={() => {
                        setDeviceFilters({ ...deviceFilters, searchText: '' });
                        loadDevices({ ...deviceFilters, searchText: '' });
                    }}
                    placeholder={translate.devices.searchPlaceholder}
                    suffix={suffixProp}
                    value={deviceFilters.searchText ?? ''}
                    variant="outline"
                />
                <DropdownFilter
                    count={deviceFilters.episodeCategories?.length}
                    label={translate.devices.filterOnEpisodeCategory}
                    onReset={() => {
                        setDeviceFilters({ ...deviceFilters, episodeCategories: [] });
                        loadDevices({ ...deviceFilters, episodeCategories: [] });
                    }}
                    onSubmit={applySearchFilter}
                >
                    {renderEpisodeCategoryDropdown()}
                </DropdownFilter>
                <DropdownFilter
                    count={deviceFilters.deviceTypes?.length}
                    label="Filter op Device"
                    onReset={() => {
                        setDeviceFilters({ ...deviceFilters, deviceTypes: [] });
                        loadDevices({ ...deviceFilters, deviceTypes: [] });
                    }}
                    onSubmit={applySearchFilter}
                >
                    {renderDeviceTypeListStatusDropdown()}
                </DropdownFilter>
                <DropdownFilter
                    count={deviceFilters.deviceStatuses?.length}
                    label="Filter op Status"
                    onReset={() => {
                        setDeviceFilters({ ...deviceFilters, deviceStatuses: [] });
                        loadDevices({ ...deviceFilters, deviceStatuses: [] });
                    }}
                    onSubmit={applySearchFilter}
                >
                    {renderStatusDropdown()}
                </DropdownFilter>
                <Box
                    alignSelf="center"
                    ml={4}
                >
                    <Text
                        size="sm"
                        whiteSpace="nowrap"
                    >
                        <Link
                            color="subtle"
                            fontWeight="normal"
                            onClick={clearFilters}
                            variant="button"
                        >
                            {translate.common.deleteAll}
                        </Link>
                    </Text>
                </Box>
            </StyledBox>
            {items.length > 0 && !isExporting && (
                <>
                    <TableContainer>
                        <Table spacing="medium">
                            <TableHead>
                                <TableRow>
                                    <TableHeaderSelectAll
                                        checked={selectedItems.length > 0 || areAllChecked}
                                        indeterminate={!areAllChecked}
                                        onSelect={() => {
                                            setSelectedItems([]);
                                            setAreAllChecked(!areAllChecked);
                                        }}
                                    />
                                    <TableHeader>{translate.devices.device}</TableHeader>
                                    <TableHeader>{translate.devices.serialNumber}</TableHeader>
                                    <TableHeader>{translate.episodeCategory.episodeCategory}</TableHeader>
                                    <TableHeader>{translate.common.status}</TableHeader>
                                    <TableHeader>{translate.organization.organization}</TableHeader>
                                    <TableHeader>{translate.organization.organization} ID</TableHeader>
                                    <TableHeader>{translate.devices.issueDate}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item) => (
                                    <TableRow
                                        key={item.id}
                                        onClick={() =>
                                            isAddEditAllowed ? navigate(`/devices/details/${item.id}`) : undefined
                                        }
                                    >
                                        <TableCellSelectRow
                                            isSelected={isRowSelected(item.id) || areAllChecked}
                                            onClick={(e) => e.stopPropagation()}
                                            onSelect={() => handleSelectRow(item.id)}
                                        />
                                        <TableCell>
                                            <TextWithMatchingTermInBold text={item.deviceType.name} />
                                        </TableCell>
                                        <TableCell>
                                            <TextWithMatchingTermInBold
                                                searchTerm={deviceFilters.searchText?.replace(/^sn/i, '')}
                                                text={item.externalId}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextWithMatchingTermInBold
                                                text={
                                                    item.deviceType.services
                                                        ? Array.from(
                                                              new Set<string>(
                                                                  item.deviceType.services
                                                                      .map(
                                                                          (service) =>
                                                                              translate.episodeCategoryEnum[
                                                                                  service.episodeCategoryName
                                                                              ]
                                                                      )
                                                                      .sort((a, b) => a.localeCompare(b))
                                                              )
                                                          ).join(', ')
                                                        : ''
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextWithMatchingTermInBold
                                                text={translate.deviceStatusEnum[item.deviceStatus.status]}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {item.deviceStatusOrganizations.length > 0 ? (
                                                <>
                                                    {item.deviceStatusOrganizations.map((deviceOrganization) => {
                                                        return (
                                                            <Row key={deviceOrganization.id}>
                                                                <TextWithMatchingTermInBold
                                                                    searchTerm={deviceFilters.searchText}
                                                                    text={deviceOrganization.organization?.name ?? ''}
                                                                />
                                                            </Row>
                                                        );
                                                    })}
                                                </>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {item.deviceStatusOrganizations.length > 0 ? (
                                                <>
                                                    {item.deviceStatusOrganizations.map((deviceOrganization) => {
                                                        return (
                                                            <Row key={deviceOrganization.id}>
                                                                <TextWithMatchingTermInBold
                                                                    searchTerm={deviceFilters.searchText}
                                                                    text={deviceOrganization.organization?.txcsId ?? ''}
                                                                />
                                                            </Row>
                                                        );
                                                    })}
                                                </>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {item.deviceStatusOrganizations.length > 0 ? (
                                                <>
                                                    {item.deviceStatusOrganizations.map((deviceOrganization) => {
                                                        return (
                                                            <Row key={deviceOrganization.id}>
                                                                <NullableFormattedDate
                                                                    value={deviceOrganization.issueDate}
                                                                />
                                                            </Row>
                                                        );
                                                    })}
                                                </>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Pagination
                        onChange={(params) => {
                            setPage(params.page);
                            setItemsPerPage(params.pageSize);
                        }}
                        page={page}
                        pageSize={itemsPerPage}
                        totalItems={total}
                    />
                </>
            )}
            {items?.length === 0 && !isSearching && (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                >
                    <Figure
                        alt="Not found"
                        centered
                        imageUrl="../../../../../assets/images/not-found-search.svg"
                    />
                    <Heading
                        mb={6}
                        size="medium"
                        textAlign="center"
                    >
                        {translate.common.noSearchResults}
                    </Heading>
                    <ButtonRow justify="center">
                        <Button onClick={clearFilters}>{translate.common.deleteSearchTerms}</Button>
                    </ButtonRow>
                </Box>
            )}
            <ActionBar
                isOpen={selectedItems.length !== 0 || areAllChecked}
                onClose={cleanSelectedRows}
            >
                <Box
                    alignItems="center"
                    display="flex"
                    gap={3}
                    justifyContent="space-between"
                >
                    <BadgeWithLabel
                        color="inverse"
                        label={translate.common.isSelected}
                        value={areAllChecked ? total.toString() : selectedItems.length.toString()}
                    />
                    <Button
                        onClick={async () => await handleExport()}
                        variant="solid"
                    >
                        {translate.common.export}
                    </Button>
                </Box>
            </ActionBar>
            {isExporting && (
                <Loader
                    className="mt-3 mx-auto"
                    size="large"
                />
            )}
        </>
    );

    async function loadDevices(manualFilters?: IDeviceFilters) {
        const urlBuilder = new UrlBuilder('/api/devices');

        const filters = manualFilters ?? deviceFilters;

        urlBuilder.add('page', page);
        urlBuilder.add('limit', itemsPerPage);

        if (filters.searchText) {
            urlBuilder.add('searchText', filters.searchText);
        }
        if (filters.episodeCategories?.length) {
            urlBuilder.add('episodeCategories', filters.episodeCategories);
        }
        if (filters.deviceStatuses?.length) {
            urlBuilder.add('statuses', filters.deviceStatuses as string[]);
        }
        if (filters.deviceTypes?.length) {
            urlBuilder.add('deviceTypes', filters.deviceTypes);
        }

        try {
            setIsSearching(true);
            const result = await abortableFetch.fetchJson(urlBuilder.build(), 'GET');
            const devices = result.json.items;
            for (const device of devices) {
                device.deviceType.services = [
                    ...device.deviceType.providingServices,
                    ...device.deviceType.consumingServices,
                ];
            }

            setItems(devices);
            setTotal(result.json.total);
            setShouldBeReloaded(false);
        } finally {
            setIsSearching(false);
        }
    }

    async function loadDeviceTypes() {
        const result = await abortableFetch.fetchJson('/api/deviceTypes', 'GET');
        const deviceTypes = result.json.items;
        for (const deviceType of deviceTypes) {
            deviceType.services = [...deviceType.providingServices, ...deviceType.consumingServices];
        }
        setDeviceTypeList(deviceTypes);
    }

    async function loadEpisodeCategoryFilterValues() {
        const result = await fetchJson('/api/devices/getEpisodeCategoryFilterValues', 'GET');
        const episodeCategories = result.json.items.map((item: { name: string }) => item.name);
        setEpisodeCategoryFilterValues(episodeCategories);
    }

    function applySearchFilter() {
        if (page !== 1) {
            setPage(1);
        } else {
            loadDevices();
        }
    }

    function renderStatusDropdown() {
        return Object.values(DeviceStatus)
            .sort((a, b) => a.localeCompare(b))
            .map((status) => {
                return (
                    <Box
                        display="flex"
                        key={status}
                        pb={2}
                    >
                        <Checkbox
                            checked={!!deviceFilters.deviceStatuses?.includes(status)}
                            key={status}
                            label={translate.deviceStatusEnum[status]}
                            onChange={(event) => {
                                const updatedDevicesStatus = [...(deviceFilters.deviceStatuses ?? [])];
                                if (event.target.checked) {
                                    updatedDevicesStatus.push(status);
                                } else {
                                    const index = updatedDevicesStatus.indexOf(status);
                                    updatedDevicesStatus.splice(index, 1);
                                }
                                handleChangeSearchFilter('deviceStatuses', updatedDevicesStatus);
                            }}
                        />
                    </Box>
                );
            });
    }

    function renderDeviceTypeListStatusDropdown() {
        return [...deviceTypeList]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((deviceType) => {
                return (
                    <Box
                        display="flex"
                        key={deviceType.id}
                        pb={2}
                    >
                        <Checkbox
                            checked={!!deviceFilters.deviceTypes?.includes(deviceType.id)}
                            key={deviceType.id}
                            label={deviceType.name}
                            onChange={(event) => {
                                const updatedDevicesTypes = [...(deviceFilters.deviceTypes ?? [])];
                                if (event.target.checked) {
                                    updatedDevicesTypes.push(deviceType.id);
                                } else {
                                    const index = updatedDevicesTypes.indexOf(deviceType.id);
                                    updatedDevicesTypes.splice(index, 1);
                                }
                                handleChangeSearchFilter('deviceTypes', updatedDevicesTypes);
                            }}
                        />
                    </Box>
                );
            });
    }

    function renderEpisodeCategoryDropdown() {
        return episodeCategoryFilterValues.map((episodeCategory) => {
            return (
                <Box
                    display="flex"
                    key={episodeCategory}
                    pb={2}
                >
                    <Checkbox
                        checked={!!deviceFilters.episodeCategories?.includes(episodeCategory)}
                        key={episodeCategory}
                        label={translate.episodeCategoryEnum[episodeCategory]}
                        onChange={(event) => {
                            const updatedEpisodeCategories = [...(deviceFilters.episodeCategories ?? [])];
                            if (event.target.checked) {
                                updatedEpisodeCategories.push(episodeCategory);
                            } else {
                                const index = updatedEpisodeCategories.indexOf(episodeCategory);
                                updatedEpisodeCategories.splice(index, 1);
                            }
                            handleChangeSearchFilter('episodeCategories', updatedEpisodeCategories);
                        }}
                    />
                </Box>
            );
        });
    }

    function handleChangeSearchFilter<K extends keyof IDeviceFilters>(name: K, value: IDeviceFilters[K]) {
        setDeviceFilters({ ...deviceFilters, [name]: value });
    }

    function clearFilters() {
        setDeviceFilters({});
        loadDevices(emptyDeviceFilters);
    }

    function isRowSelected(selectedId: string) {
        return !!selectedItems.find((id) => id === selectedId);
    }

    function handleSelectRow(id: string) {
        let newSelectedItems = [...selectedItems];

        if (areAllChecked) {
            newSelectedItems = items.map((device) => device.id).filter((deviceId) => deviceId !== id);
            setAreAllChecked(false);
        } else if (isRowSelected(id)) {
            const index = newSelectedItems.indexOf(id);
            newSelectedItems.splice(index, 1);
        } else {
            newSelectedItems.push(id);
        }

        setSelectedItems(newSelectedItems);
    }

    function cleanSelectedRows() {
        setSelectedItems([]);
        setAreAllChecked(false);
    }

    async function handleExport() {
        cleanSelectedRows();
        setIsExporting(true);
        const itemsToExport = areAllChecked ? undefined : selectedItems;

        const result = await fetchJson('/api/devices/exportList', 'POST', { deviceFilters, itemsToExport });
        window.open(`/api/exportDownloads/${result.json.exportDownloadId}/devices.xlsx`);

        setIsExporting(false);
        applySearchFilter();
    }
}
